import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FieldLabel } from './FieldLabel'
import { useNetwork } from '../contexts/NetworkContext'
import type { AdUnit } from '@/services/adUnits/adUnitsApi'
import { downloadAdUnitTags, type AdUnitTagDownload, type GdprApplies } from '@/services/adUnits/adUnitTags'
import { adTagTemplates, vastVersions } from '@/data/adTagTemplates'

interface AdUnitTagDownloadDialogProps {
  adUnits: AdUnit[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Downloads the ad tags or VAST tags for a set of ad units. */
export function AdUnitTagDownloadDialog({ adUnits, open, onOpenChange }: AdUnitTagDownloadDialogProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const [type, setType] = useState<AdUnitTagDownload>('INDIVIDUAL_REQUESTS')
  const [templateId, setTemplateId] = useState(adTagTemplates[0].id)
  const [vastVersion, setVastVersion] = useState('vast4')
  const [gdpr, setGdpr] = useState<GdprApplies>('UNSET')
  const [busy, setBusy] = useState(false)

  const types: { id: AdUnitTagDownload; label: string; help: string }[] = [
    { id: 'INDIVIDUAL_REQUESTS', label: t('adUnitTags.individual'), help: t('adUnitTags.individualHelp') },
    { id: 'SINGLE_REQUEST', label: t('adUnitTags.single'), help: t('adUnitTags.singleHelp') },
    { id: 'VAST', label: t('adUnitTags.vast'), help: t('adUnitTags.vastHelp') },
  ]
  const gdprOptions: { id: GdprApplies; label: string }[] = [
    { id: 'UNSET', label: t('adUnitTags.gdprUnset') },
    { id: 'APPLIES', label: t('adUnitTags.gdprApplies') },
    { id: 'NOT_APPLIES', label: t('adUnitTags.gdprNotApplies') },
  ]

  const download = async () => {
    if (!currentNetwork) return
    setBusy(true)
    try {
      await downloadAdUnitTags(type, adUnits, {
        networkTagId: currentNetwork.tagId,
        gdpr,
        templateId,
        vastVersion,
        fileBase: 'ad-units',
      })
      onOpenChange(false)
    } catch {
      toast.error(t('adUnitTags.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('adUnitTags.title')}</DialogTitle>
          <DialogDescription>{t('adUnitTags.description', { count: adUnits.length })}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="p-field">
            <FieldLabel>{t('adUnitTags.type')}</FieldLabel>
            <RadioGroup value={type} onValueChange={(v) => setType(v as AdUnitTagDownload)} className="gap-2">
              {types.map((option) => (
                <div key={option.id} className="flex items-start gap-2">
                  <RadioGroupItem value={option.id} id={`au-tag-${option.id}`} className="mt-0.5" />
                  <Label htmlFor={`au-tag-${option.id}`} className="flex-col items-start gap-0.5">
                    <span className="text-sm font-medium">{option.label}</span>
                    <span className="text-xs font-normal text-muted-foreground">{option.help}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {type === 'VAST' ? (
            <div className="p-field">
              <FieldLabel>{t('adUnitTags.vastVersion')}</FieldLabel>
              <Select value={vastVersion} onValueChange={setVastVersion}>
                <SelectTrigger>
                  <SelectValue>{vastVersions.find((v) => v.id === vastVersion)?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {vastVersions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="p-field">
              <FieldLabel tooltip={adTagTemplates.find((tpl) => tpl.id === templateId)?.description}>
                {t('adUnits.tagType')}
              </FieldLabel>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger>
                  <SelectValue>{adTagTemplates.find((tpl) => tpl.id === templateId)?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {adTagTemplates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="p-field">
            <FieldLabel tooltip={t('adUnitTags.gdprHelp')}>{t('adUnitTags.gdpr')}</FieldLabel>
            <Select value={gdpr} onValueChange={(v) => setGdpr(v as GdprApplies)}>
              <SelectTrigger>
                <SelectValue>{gdprOptions.find((o) => o.id === gdpr)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {gdprOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={download} loading={busy} disabled={!currentNetwork || adUnits.length === 0}>
            <Download className="size-4" />
            {t('adUnitTags.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
